<script lang="ts">
  // import Border from "./border.svelte";
  import { onMount, onDestroy, tick } from 'svelte';
  import { claim, release, isTouchDevice, measuringStyle, placeTip } from './tip';

  interface Props {
    // How a touch opens the tip. 'hold' presses and holds; 'tap' opens on a
    // tap and closes on the next one. Hold is the default because some tips
    // sit on things a tap already does something to -- choosing ammo, using
    // an item.
    touch?: 'hold' | 'tap';
    // Extra reach around a small target on a touch screen, in pixels. The
    // target looks the same; only the area a finger can land in grows.
    reach?: number;
    children?: import('svelte').Snippet;
  }

  let { touch = 'hold', reach = 0, children }: Props = $props();

  let hovered = $state(false);
  let touched = $state(false);
  let fading = $state(false);
  let hoverEl: HTMLElement | null = $state(null);
  let tipEl: HTMLElement | null = $state(null);
  // Where the tip goes, worked out in tip.ts once it can be measured.
  let placement = $state(measuringStyle);
  /** Teardown functions returned by createListener, run on destroy. */
  let events: Array<() => void> = [];

  function createListener(element: EventTarget, eventName: string, func: EventListener) {
    element.addEventListener(eventName, func);

    return () => {
      element.removeEventListener(eventName, func);
    };
  }

  async function place() {
    placement = measuringStyle;
    await tick();
    if (hovered && hoverEl && tipEl) {
      placement = placeTip(hoverEl, tipEl);
    }
  }

  function open() {
    if (!hoverEl) {
      return;
    }
    hovered = true;
    fading = false;
    // One tip open at a time, whichever kind. A touch elsewhere closes this
    // one through the single listener tip.ts keeps on the document.
    claim({ anchor: hoverEl, close });
    place();
  }

  function close() {
    if (!hovered) {
      return;
    }
    release(close);
    hovered = false;
    fading = true;

    setTimeout(() => {
      fading = false;
    }, 300);
  }

  onMount(() => {
    // bind:this has run by now; bail rather than attaching to nothing.
    if (!hoverEl) {
      return;
    }

    if (isTouchDevice) {
      if (touch == 'tap') {
        events.push(
          createListener(hoverEl, 'click', () => {
            hovered ? close() : open();
          }),
        );
      } else {
        var timeout: any = null;

        events.push(
          createListener(hoverEl, 'touchstart', () => {
            touched = true;

            timeout = setTimeout(() => {
              open();
            }, 500);
          }),
        );

        events.push(
          createListener(hoverEl, 'touchend', () => {
            if (!hovered) {
              clearTimeout(timeout);
            }
            touched = false;
          }),
        );
      }
    } else {
      events.push(createListener(hoverEl, 'mouseenter', open));
      events.push(createListener(hoverEl, 'mouseleave', close));
    }
  });

  onDestroy(() => {
    release(close);
    events.forEach((event: () => void) => {
      event();
    });
    events.length = 0;
  });
</script>

<div
  class="hover-area"
  class:pressing={touched && !hovered}
  style={isTouchDevice && reach ? `inset: -${reach}px; width: auto; height: auto;` : ''}
  bind:this={hoverEl}
>
  {#if hovered || fading}
    <div class="shadow {fading ? 'fading' : ''}"></div>

    <div class="hover-container {fading ? 'fading' : ''}" style={placement} bind:this={tipEl}>
      <!-- <Border borderType='fog'/> -->

      <div class="hover">
        {@render children?.()}
      </div>
    </div>
  {/if}
</div>

<style lang="less">
  .hover-area {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 111;
    border-radius: 6px;
    transition: background-color 0.2s;

    // A held finger gets a response straight away, rather than half a second
    // of nothing before the tip appears.
    &.pressing {
      background-color: rgba(255, 255, 255, 0.12);
    }

    .hover-container {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      max-width: min(420px, calc(100vw - 16px));
      box-sizing: border-box;
      pointer-events: none;
      z-index: 111;

      border-image: url('../../assets/shadow.png') 10 / 10px;
      border-width: 7px;
      border-style: solid;

      animation: fadeIn 0.3s;
      animation-fill-mode: forwards;

      &.fading {
        animation: fadeOut 0.3s;
      }

      .hover {
        background-color: var(--background-colour);
        outline: 1px solid black;
        border-radius: 4px;
        line-height: 1;
        padding: 20px;
      }
    }

    .shadow {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background: rgba(0, 0, 0, 0.3);
      z-index: 11;
      animation: fadeIn 0.3s;
      animation-fill-mode: forwards;

      &.fading {
        animation: fadeOut 0.3s;
      }
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
</style>
